import qs from "qs";
import to from "await-to-js";
import vars from "utils/vars";
import { makeFetchError, parseFetchResponseBody, withAuthAndTenantHeaders } from "./skyflowFetchHelpers";

const VAULT_ID = vars.secrets.skyflow.vaultID;
const VAULT_URL = vars.secrets.skyflow.vaultURL;

const getSkyflowProxyPrefix = () => {
	const headers = vars.api.headers || {};
	// If user is logged in, use auth proxy (full policy).
	// NOTE: these proxy calls use `fetch`, so we must read the token ourselves (axios interceptors won't apply).
	const tokenKey = vars?.localStorage?.fieldsName?.token || "token";
	const token = typeof window !== "undefined" ? window?.localStorage?.getItem(tokenKey) : null;
	if (token || headers.Authorization || headers.authorization) return "auth";

	// Backend no longer uses `X-API-KEY` / `X-DOCS-KEY` for routing, so we should not either.
	// Default public context is patient intake.
	return "intake";
};

const skyflowProxyBaseUrl = (prefixOverride) =>
	`${vars.app.baseUrl}/skyflow-proxy/${prefixOverride || getSkyflowProxyPrefix()}`;

/** Tables where bare `id` is mapped to `filter[0][column]=skyflow_id` (Vaulto proxy also requires `id` in the query). */
const TABLES_WHERE_ID_MAPS_TO_SKYFLOW_ID_FILTER = new Set([
	vars.app.skyflowTables.patientOrder,
	vars.app.skyflowTables.patientChildOrder,
	vars.app.skyflowTables.users,
]);

// Skyflow Elements token removed: we do not allow Skyflow bearer tokens to reach the browser.
// Shared fetch helpers (tenant/auth headers, body parsing, axios-shaped errors) live in
// `./skyflowFetchHelpers` so `postPatientDocument.js` can reuse them without duplication.

export const postSkyflowFileUpload = async ({ table, skyflowID, body, proxyPrefix }) =>
	new Promise(async (resolve, reject) => {
		// Backend expects:
		// - multipart `file` field
		// - a columnName (used as Skyflow `column_name`)
		// Existing callers often build FormData like `{ [columnName]: file }` (legacy skyflow-js style).
		const normalizeUploadFormData = () => {
			if (!(body instanceof FormData)) return body instanceof FormData ? body : new FormData();

			// If caller already provides `file`, keep as-is.
			if (body.has("file")) return body;

			let detectedColumnName = null;
			let detectedFile = null;

			for (const [key, value] of body.entries()) {
				if (value instanceof File || value instanceof Blob) {
					detectedColumnName = key;
					detectedFile = value;
					break;
				}
			}

			if (!detectedFile || !detectedColumnName) return body;

			const next = new FormData();
			next.append("file", detectedFile, detectedFile?.name || "upload");
			next.append("columnName", detectedColumnName);
			return next;
		};

		const normalizedBody = normalizeUploadFormData();
		// IMPORTANT: never send `Content-Type: application/json` with multipart uploads.
		// Let the browser set `multipart/form-data; boundary=...`.
		const uploadHeaders = (() => {
			const h = withAuthAndTenantHeaders({ ...(vars.api.headers || {}) });
			// Support both casing styles
			delete h["content-type"];
			delete h["Content-Type"];
			return h;
		})();

		const [error, response] = await to(
			fetch(`${skyflowProxyBaseUrl(proxyPrefix)}/files/upload`, {
				method: "POST",
				headers: uploadHeaders,
				body: (() => {
					const form = normalizedBody instanceof FormData ? normalizedBody : new FormData();
					form.append("table", table);
					form.append("id", skyflowID);
					return form;
				})(),
			})
		);

		if (error) reject(error);
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		resolve(data);
	});

export const deleteSkyflowFile = async ({ table, skyflowID, columnName, proxyPrefix }) =>
	new Promise(async (resolve, reject) => {
		const [error, response] = await to(
			fetch(`${skyflowProxyBaseUrl(proxyPrefix)}/files`, {
				method: "DELETE",
				headers: withAuthAndTenantHeaders({
					"Content-Type": "application/json",
					...(vars.api.headers || {}),
				}),
				body: JSON.stringify({ table, id: skyflowID, columnName }),
			})
		);

		if (error) reject(error);
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		resolve(data);
	});

/**
 * Resolves Skyflow row id from POST /skyflow-proxy/.../records insert responses.
 * Proxy may return `records[0].fields.skyflow_id`, `records[0].data.skyflow_id`, top-level ids, or `data[]`.
 */
export const extractSkyflowIdFromInsertResponse = (response = {}) => {
	if (!response || typeof response !== "object") return "";

	const fromRecord = (rec) => {
		if (!rec || typeof rec !== "object") return "";
		const v = rec?.data?.skyflow_id ?? rec?.fields?.skyflow_id ?? rec?.skyflow_id ?? rec?.skyflowID;
		return v != null && String(v).trim() ? String(v).trim() : "";
	};

	const firstRecord = response?.records?.[0] ?? (Array.isArray(response?.data) ? response.data[0] : undefined);
	const fromFirst = fromRecord(firstRecord);
	if (fromFirst) return fromFirst;

	const top =
		response?.skyflow_id ??
		response?.skyflowID ??
		(response?.data &&
			typeof response.data === "object" &&
			!Array.isArray(response.data) &&
			(response.data.skyflow_id ?? response.data.skyflowID));

	if (top != null && String(top).trim()) return String(top).trim();

	return "";
};

export const postSkyflowRecords = async ({ table, body, proxyPrefix }) =>
	new Promise(async (resolve, reject) => {
		const payload =
			typeof body === "string"
				? { table, ...JSON.parse(body) }
				: {
						table,
						...(body || {}),
				  };
		const [error, response] = await to(
			fetch(`${skyflowProxyBaseUrl(proxyPrefix)}/records`, {
				method: "POST",
				headers: withAuthAndTenantHeaders({
					"Content-Type": "application/json",
					...(vars.api.headers || {}),
				}),
				body: JSON.stringify(payload),
			})
		);

		if (error) reject(error);
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		resolve(data);
	});

export const putSkyflowRecords = async ({ table, body, proxyPrefix }) =>
	new Promise(async (resolve, reject) => {
		const payload =
			typeof body === "string"
				? { table, ...JSON.parse(body) }
				: {
						table,
						...(body || {}),
				  };

		const requestHeaders = withAuthAndTenantHeaders({
			"Content-Type": "application/json",
			...(vars.api.headers || {}),
		});
		let resolvedPrefix = proxyPrefix ?? getSkyflowProxyPrefix();
		// Intake proxy has no PUT /records (Laravel only registers POST, DELETE there).
		const hasBearer = Boolean(requestHeaders.Authorization) || Boolean(requestHeaders.authorization);
		if (resolvedPrefix === "intake" && hasBearer) {
			resolvedPrefix = "auth";
		}

		const [error, response] = await to(
			fetch(`${skyflowProxyBaseUrl(resolvedPrefix)}/records`, {
				method: "PUT",
				headers: requestHeaders,
				body: JSON.stringify(payload),
			})
		);

		if (error) reject(error);
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		resolve(data);
	});

export const deleteSkyflowRecord = async ({ table, skyflowID, proxyPrefix }) =>
	new Promise(async (resolve, reject) => {
		const [error, response] = await to(
			fetch(`${skyflowProxyBaseUrl(proxyPrefix)}/records`, {
				method: "DELETE",
				headers: withAuthAndTenantHeaders({
					"Content-Type": "application/json",
					...(vars.api.headers || {}),
				}),
				body: JSON.stringify({ table, id: skyflowID }),
			})
		);

		if (error) reject(error);
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		resolve(data);
	});

// redaction is forwarded to the backend so proxy returns plain text instead of REDACTED when allowed by policy.
// `idQueryOnly`: with bare `id`, omit skyflow_id `filter[0]`; `id` is still sent as a query param when provided.
export const getSkyflowRecord = ({
	table,
	id,
	proxyPrefix,
	redaction,
	column_name,
	column_values,
	idQueryOnly,
	...fields
}) =>
	new Promise(async (resolve, reject) => {
		const safeId = (() => {
			if (id === undefined || id === null) return null;
			const raw = String(id).trim();
			if (!raw) return null;
			const lower = raw.toLowerCase();
			if (lower === "undefined" || lower === "null") return null;
			return raw;
		})();

		// Send one filter entry: filter[0][column] + filter[0][value]. Use comma-separated string for multiple IDs so the URL stays short and the backend receives all values.
		const filterParams = {};
		if (column_name && column_values != null && column_values !== "") {
			const vals = [
				...new Set(
					(Array.isArray(column_values) ? column_values : [].concat(column_values))
						.filter(Boolean)
						.map(String)
				),
			];
			filterParams["filter[0][column]"] = column_name;
			filterParams["filter[0][value]"] = vals.length > 1 ? vals.join(",") : vals[0] ?? column_values;
		}

		// Without an explicit column filter, map bare `id` to skyflow_id for known tables (auth proxy contract).
		if (
			safeId &&
			!idQueryOnly &&
			!filterParams["filter[0][column]"] &&
			table &&
			TABLES_WHERE_ID_MAPS_TO_SKYFLOW_ID_FILTER.has(table)
		) {
			filterParams["filter[0][column]"] = "skyflow_id";
			filterParams["filter[0][value]"] = safeId;
		}

		const queryPayload = {
			table,
			...filterParams,
			redaction: redaction || "PLAIN_TEXT",
			...fields,
		};

		// Intake/auth/docs proxies require `id` when fetching by record id (filter alone is not enough).
		if (safeId) {
			queryPayload.id = safeId;
		}

		const url = `${skyflowProxyBaseUrl(proxyPrefix)}/records/get?${qs.stringify(queryPayload)}`;

		const [error, response] = await to(
			fetch(url, {
				headers: withAuthAndTenantHeaders({ ...(vars.api.headers || {}) }),
			})
		);

		if (error) {
			// fetch() throws on network failures (connection refused, DNS, mixed-content, etc.)
			// Add high-signal context to make local/iframe debugging easier.
			const err = new Error(`${error?.message || "Failed to fetch"} (Skyflow proxy). URL: ${url}`);
			err.cause = error;
			reject(err);
			return;
		}
		if (!response?.ok) {
			const payload = await parseFetchResponseBody(response);
			reject(makeFetchError(response, payload));
			return;
		}

		const data = (await parseFetchResponseBody(response)) || {};
		// Proxy returns { success, pagination, data: [ flat rows ] }. Map to { records: [ { fields } ] } for app.
		if (!Array.isArray(data?.records)) {
			if (Array.isArray(data?.data)) {
				data.records = data.data.map((row) => ({
					fields: row && typeof row === "object" ? row : {},
				}));
			} else if (data?.data && typeof data.data === "object") {
				data.records = [{ fields: data.data }];
			}
		}
		resolve(data);
	});

const skyflowConfig = {
	vaultID: VAULT_ID, // Id of the vault that the client should connect to.
	vaultURL: VAULT_URL, // URL of the vault that the client should connect to.
	// Skyflow Elements token removed: we do not allow Skyflow bearer tokens to reach the browser.
};

export default skyflowConfig;
